import { Icon } from '@buggy/shared'
import { Flex, Skeleton } from 'antd'
import { FunctionComponent, ReactNode } from 'react'
import styled, {
  css,
  DefaultTheme,
  FlattenInterpolation,
  ThemeProps,
} from 'styled-components'
import { PlatformData } from '@/components/kol/navigation/platform-select'
import { useIntl } from '@/i18n/hooks/use-intl'
import { PlatformShortcode, supportedPlatforms } from '@/utils/convert-platform'

interface PlatformTabsProps {
  isUpdating?: boolean
  isFetching?: boolean
  activePlatformShortcode?: PlatformShortcode
  platforms: PlatformData[]
  handlePlatformChange: (platformShortCode: PlatformShortcode) => void
}

const PlatformTabSelector: FunctionComponent<PlatformTabsProps> = ({
  isUpdating,
  activePlatformShortcode,
  platforms,
  handlePlatformChange,
  isFetching,
}) => {
  const { formatMessage, formatNumber } = useIntl()

  return (
    <Wrapper>
      {((): ReactNode => {
        if (isFetching) {
          return (
            <Flex gap={10}>
              {supportedPlatforms.map(({ shortcode }) => (
                <StyledSkeleton
                  active
                  key={shortcode}
                  paragraph={{ rows: 1 }}
                  title={false}
                />
              ))}
            </Flex>
          )
        }

        if (isUpdating) {
          return supportedPlatforms.map(
            ({ shortcode, color, sharedIconName }) => {
              const isActive = shortcode === activePlatformShortcode
              return (
                <Tab
                  $isActive={isActive}
                  $platformColor={color}
                  key={shortcode}
                  onClick={(): void => handlePlatformChange(shortcode)}
                >
                  {sharedIconName && (
                    <Icon fontSize={isActive ? 24 : 14} name={sharedIconName} />
                  )}
                  <div>
                    {formatMessage({ id: 'campaign:crawler_status_running' })}
                  </div>
                </Tab>
              )
            },
          )
        }

        return platforms.map(
          ({ shortcode, color, sharedIconName, follower }) => {
            const isActive = shortcode === activePlatformShortcode
            return (
              <Tab
                $isActive={isActive}
                $platformColor={color}
                key={shortcode}
                onClick={(): void => handlePlatformChange(shortcode)}
              >
                {sharedIconName && (
                  <Icon fontSize={isActive ? 24 : 14} name={sharedIconName} />
                )}
                <div>
                  {follower
                    ? formatNumber(follower, {
                        isCompactDisplay: true,
                      })
                    : formatMessage({
                        id: 'kol:data_platform_no_dosclosure',
                      })}
                </div>
              </Tab>
            )
          },
        )
      })()}
    </Wrapper>
  )
}

export default PlatformTabSelector

const StyledSkeleton = styled(Skeleton)`
  width: 59px;

  ul {
    margin: 0 !important;
  }

  li {
    width: unset !important;
    height: 16px !important;
  }
`

const Tab = styled.button<{
  $isActive: boolean
  $platformColor?: string
}>`
  display: flex;
  align-items: center;
  padding: 4px 15px;
  border: none;
  gap: 5px;

  ${({
    $isActive,
    $platformColor,
  }): FlattenInterpolation<ThemeProps<DefaultTheme>> =>
    $isActive
      ? css`
          border-radius: 21px;
          background: ${$platformColor};

          > div {
            color: ${({ theme }): string => theme.colors.text.white};
            font-size: 16px;
            font-weight: 500;
            line-height: 28px;
          }
        `
      : css`
          border: 1px solid ${({ theme }): string => theme.colors.border.border};
          border-radius: 25px;
          background: ${({ theme }): string => theme.colors.background.white};
          cursor: pointer;

          > div {
            color: ${({ theme }): string => theme.colors.text.tooltip};
            font-size: 14px;
            font-weight: 400;
            line-height: 28px;
          }

          &:hover {
            background: ${({ theme }): string => theme.colors.background.grey};

            > div {
              color: ${({ theme }): string => theme.colors.text.primary};
              font-weight: 500;
            }
          }
        `}

  > div {
    font-style: normal;
  }
`

const Wrapper = styled.div`
  display: flex;
  gap: 10px;
`
